/**
 * DraftInvoicesWidget (R3)
 * Shows a list of draft invoices sorted oldest first.
 * Each row links to the individual invoice. Header links to the full draft list.
 *
 * This is a parallel widget — does NOT touch the draft count in UpcomingTasks.tsx.
 */
import { useDraftInvoices, DraftInvoiceItem } from '@/hooks/useDashboardReports';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { FileText, ExternalLink } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { formatDateOnly } from '@/lib/utils';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
}

function formatDate(isoDate: string | null): string {
  if (!isoDate) return '—';
  // invoice_date is a DATE column (no time component). Parsing it with the
  // bare `new Date(isoDate)` constructor treats it as UTC midnight, which
  // renders one calendar day early for anyone west of UTC (e.g. America/Edmonton).
  // formatDateOnly (src/lib/utils.ts) parses the y/m/d components directly so
  // the displayed date always matches the stored calendar date.
  return formatDateOnly(isoDate, { month: 'short', day: 'numeric' });
}

export function DraftInvoicesWidget() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useDraftInvoices();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5" />
            Draft Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 animate-pulse">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5" />
            Draft Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load draft invoices</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="draft-invoices-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5" />
            Draft Invoices
          </CardTitle>
          <div className="flex items-center gap-2">
            {data.count > 0 && (
              <Badge variant="secondary">{data.count}</Badge>
            )}
            <Link
              to="/invoices?status=draft"
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5 transition-colors"
            >
              View all <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No draft invoices</p>
        ) : (
          <div className="space-y-2">
            {data.data.map((invoice: DraftInvoiceItem) => (
              <div
                key={invoice.invoice_id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/invoices/${invoice.invoice_id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(`/invoices/${invoice.invoice_id}`);
                  }
                }}
                className="flex items-start gap-2 p-2 rounded text-sm cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <FileText size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium text-gray-900 truncate">
                      {invoice.invoice_number || 'Draft'}
                    </span>
                    <span className="text-xs font-medium text-gray-900 flex-shrink-0">
                      {formatCurrency(invoice.total_amount)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 truncate">
                    {invoice.project_name}
                    {invoice.client_name ? ` · ${invoice.client_name}` : ''}
                    {invoice.invoice_date ? ` · ${formatDate(invoice.invoice_date)}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
